import React from 'react';

// "Ledger & Seal" family, Amazon Seller Intelligence & Action Agent
// edition -- shares the shell used by DynamicPricingOnePager,
// PredictiveRestockOnePager, etc. No title/video here -- heroBanner: true
// handles that (see useCases.js), with a static heroImage mockup standing
// in until a recorded walkthrough exists.
//
// Differences from the other one-pagers: "How it works" has five stamps
// (Data -> Insight -> Recommendation -> Approval -> Action) instead of four,
// and there's an extra "Example" section walking through the brand-manager
// question from the source brief.
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  pink: '#9d174d',
};

const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 40%, ${PALETTE.indigo} 70%, ${PALETTE.violet} 100%)`;

const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const STAMP_FROM = [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violet];
const STAMP_TO = [PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violet, PALETTE.pink];

const checklist = [
  { title: 'Inventory & Restock Agent', body: 'Monitors inventory levels, sales velocity, and demand signals to flag potential stockouts or excess inventory and recommend replenishment actions.' },
  { title: 'Pricing Agent', body: 'Analyzes product performance, pricing trends, and business rules to identify pricing opportunities and recommend adjustments.' },
  { title: 'Listing Optimization Agent', body: 'Monitors listing performance and product information to surface opportunities to improve product content and merchandising.' },
  { title: 'Sales Performance Agent', body: 'Combines Amazon sales data with financial, marketing, and operational data to explain performance changes and surface opportunities.' },
  { title: 'Executive Seller Agent', body: 'Gives leadership a conversational view of Amazon performance across sales, inventory, advertising, and operations, highlighting the actions that need attention.' },
];

const stamps = [
  { num: '01', title: 'Data', body: 'Amazon seller data joined with ERP, finance, marketing, and supply chain' },
  { num: '02', title: 'Insight', body: 'Agents monitor defined conditions and explain what changed' },
  { num: '03', title: 'Recommendation', body: 'Next steps drafted against your business rules' },
  { num: '04', title: 'Approval', body: 'A human reviews and signs off within set permissions' },
  { num: '05', title: 'Action', body: 'Approved actions execute, with a full audit trail' },
];

const exampleSteps = [
  'Explains what is driving the change',
  'Recommends a replenishment plan',
  'Identifies a cross-promotion opportunity',
  'Drafts the required actions',
  'Presents them for approval',
  'Executes the approved actions',
];

// Business value pillars, taken verbatim from the use case brief (no
// numeric claims -- the brief doesn't include any metrics).
const valuePillars = [
  { title: 'Unified Intelligence', body: 'Connect Amazon seller data with the rest of your enterprise data.' },
  { title: 'Faster Decisions', body: 'Move from manually analyzing dashboards to conversational, context-aware insights.' },
  { title: 'Proactive Operations', body: 'Continuously monitor inventory, pricing, listings, and performance instead of waiting for someone to identify an issue.' },
  { title: 'Automated Workflows', body: 'Turn insights into governed actions with approval controls and auditability.' },
  { title: 'Scale Without Adding Complexity', body: 'Give teams AI agents capable of monitoring and executing repetitive operational workflows around the clock.' },
];

const AmazonSellerIntelligenceOnePager = () => {
  return (
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-6">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm"
        style={{ borderLeft: `5px solid ${PALETTE.blueLight}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">Amazon performance managed in a silo, one dashboard at a time.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Retail and CPG teams often manage Amazon separately from the rest of the business. Sales, inventory, advertising, finance, supply chain, and product data live in different systems, so teams spend hours manually reviewing seller performance, inventory levels, ad results, and pricing just to understand what is happening. The result: disconnected decisions, slower response times, and missed opportunities to optimize inventory, pricing, and growth.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">An AI-powered operating layer that moves from insight to action.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Using Amazon Quick and the Amazon Selling Partner plugin, Amazon seller intelligence is connected with the systems and data that run the rest of the business. Teams ask questions in plain language, analyze performance across sources, and deploy specialized agents that continuously monitor the business and recommend what to do next. Instead of another dashboard, agents surface recommendations and execute approved actions based on established business rules, with human approval, defined access boundaries, and audit trails keeping teams in control.
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">The agents</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8]">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.blue}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">From insight to action</h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7">
        <div
          className="absolute left-[6%] right-[6%] top-[59px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-5">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{ background: `linear-gradient(135deg, ${STAMP_FROM[i]}, ${STAMP_TO[i]})` }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
        <p className="relative mt-6 border-t border-b-0 border-l-0 border-r-0 border-dashed border-[#e7dcc8] pt-4 text-base italic text-gray-500">
          Built with Amazon Quick and the Amazon Selling Partner plugin, connected to your ERP, finance, marketing, and supply chain data.
        </p>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">Example</h3>
      <div className="mb-8 grid grid-cols-1 gap-6 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
            A brand manager asks
          </div>
          <blockquote
            className="rounded-md bg-[#faf6ee] px-5 py-4 text-lg italic text-gray-900"
            style={{ borderLeft: `4px solid ${PALETTE.indigo}` }}
          >
            &ldquo;How are our top Amazon products performing and where should we focus this week?&rdquo;
          </blockquote>
          <p className="mt-4 text-base leading-relaxed text-gray-700">
            Quick analyzes Amazon sales, inventory, and product performance alongside the company&apos;s marketing, financial, and supply chain data. It finds that a top-selling SKU is accelerating faster than forecast, its inventory is approaching threshold, and a related product is sitting on excess inventory.
          </p>
        </div>
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
            Instead of just reporting it, the agent
          </div>
          <ol className="overflow-hidden rounded-md border border-[#f1e9d8]">
            {exampleSteps.map((step, i) => (
              <li
                key={step}
                className={`flex items-center gap-4 px-4 py-3 ${i !== exampleSteps.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-white"
                  style={{ background: `linear-gradient(135deg, ${LEDGER_COLORS[Math.min(i, 3)]}, ${LEDGER_COLORS[Math.min(i + 1, 4)]})` }}
                >
                  {i + 1}
                </span>
                <span className="text-base font-semibold text-gray-800">{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <p className="border-t border-dashed border-[#e7dcc8] pt-4 text-base italic text-gray-600 lg:col-span-2">
          The result is an AI-powered operating model where teams spend less time assembling data and more time making and executing decisions.
        </p>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] lg:grid-cols-5">
        {valuePillars.map((pillar, i) => (
          <div
            key={pillar.title}
            className={`flex flex-col gap-2 px-5 py-5 ${
              i !== valuePillars.length - 1 ? 'border-b border-[#f1e9d8] lg:border-b-0 lg:border-r' : ''
            }`}
          >
            <span
              className="text-xl font-extrabold leading-tight"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[Math.min(i + 1, 4)]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {pillar.title}
            </span>
            <span className="text-base text-gray-600">{pillar.body}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AmazonSellerIntelligenceOnePager;
